import { ProductType } from '../../constants';
import type { ProductDetail, SimpleProductDetail, VariableProductDetail, VariationProductDetail } from '../../model';

/**
 * GA4 Item structure for ecommerce events
 */
export interface GA4Item {
  item_id: string;
  item_name: string;
  item_brand: string;
  item_sku: string;
  price: string | undefined;
  item_category: string;
  item_category2: string;
}

/**
 * Creates GA4 items array from product data
 * This is the core utility used by all ecommerce events (view_item, add_to_cart, etc.)
 */
export const createGA4ItemsFromProduct = (product: ProductDetail): GA4Item[] => {
  if (product.type === ProductType.Simple) {
    const simpleProduct = product as SimpleProductDetail;
    return [
      {
        item_id: simpleProduct.productId.toString(),
        item_name: simpleProduct.name,
        item_brand: simpleProduct.brand || '',
        item_sku: simpleProduct.sku,
        price: (simpleProduct.salePrice ?? simpleProduct.originalPrice)?.toString(),
        item_category: simpleProduct.category?.[0] || '',
        item_category2: simpleProduct.category?.[1] || '',
      },
    ];
  }

  const variableProduct = product as VariableProductDetail;
  return variableProduct.variations.map((variation) => ({
    item_id: variation.productId.toString(),
    item_name: variation.name,
    item_brand: variation.brand || '',
    item_sku: variation.sku,
    price: (variation.salePrice ?? variation.originalPrice)?.toString(),
    item_category: variation.category?.[0] || '',
    item_category2: variation.category?.[1] || '',
  }));
};

/**
 * Gets the initial selected option product for pricing
 */
export const getSelectedOptionProduct = (
  product: ProductDetail,
  selectedOptionProductId?: number | string,
): SimpleProductDetail | VariationProductDetail | undefined => {
  if (product.type === ProductType.Simple) {
    return product as SimpleProductDetail;
  }

  const variableProduct = product as VariableProductDetail;
  return variableProduct.variations.find(
    (variation) => String(variation.productId) === String(selectedOptionProductId),
  );
};

// ============================================
// PDP View Item Event
// ============================================

/**
 * Parameters for GA4 view_item event on Product Detail Page
 */
export interface PDPViewItemParams {
  name: string;
  'x-fb-cd-content_type': string;
  'x-fb-cd-content_name': string;
  'x-fb-cd-content_ids': string;
  value: number | undefined;
  currency: string;
  items: GA4Item[];
  event_name: 'view_item';
  device_id?: string;
  event_time: number;
  [key: string]:
    | string
    | number
    | boolean
    | undefined
    | null
    | string[]
    | GA4Item[]
    | Record<string, string | number | boolean | undefined>
    | Record<string, string | number | boolean | undefined>[];
}

export interface CreatePDPViewItemParamsInput {
  product: ProductDetail;
  initialSelectedOptionProductId?: number | string;
  currency: string;
  deviceId?: string;
}

/**
 * Creates the logging parameters for GA4 view_item event on Product Detail Page (PDP)
 */
export const createPDPViewItemParams = (input: CreatePDPViewItemParamsInput): PDPViewItemParams | undefined => {
  const { product, initialSelectedOptionProductId, currency, deviceId } = input;

  const initialSelectedOptionProduct = getSelectedOptionProduct(product, initialSelectedOptionProductId);

  if (!initialSelectedOptionProduct) {
    return undefined;
  }

  const items = createGA4ItemsFromProduct(product);

  return {
    name: 'PDP_view_item',
    'x-fb-cd-content_type': 'product',
    'x-fb-cd-content_name': product.name,
    'x-fb-cd-content_ids': product.productId,
    value: initialSelectedOptionProduct.salePrice ?? initialSelectedOptionProduct.originalPrice,
    currency,
    items,
    event_name: 'view_item',
    event_time: Math.round(new Date().getTime() / 1000),
    ...(deviceId && { device_id: deviceId }),
  };
};

// ============================================
// Add to Cart Event
// ============================================

/**
 * Parameters for GA4 add_to_cart event
 */
export interface AddToCartParams {
  'x-fb-cd-content_type': string;
  'x-fb-cd-content_name': string;
  'x-fb-cd-content_ids': string;
  value: string;
  currency: string;
  items: GA4Item[];
  event_name: 'add_to_cart';
  device_id?: string;
  event_time: number;
  item_variant: string;
  product_sku: string;
  [key: string]:
    | string
    | number
    | boolean
    | undefined
    | null
    | string[]
    | GA4Item[]
    | Record<string, string | number | boolean | undefined>
    | Record<string, string | number | boolean | undefined>[];
}

export interface CreateAddToCartParamsInput {
  product: ProductDetail;
  selectedOptionProduct: SimpleProductDetail | VariationProductDetail;
  quantity: number;
  currency: string;
  deviceId?: string;
}

/**
 * Creates the logging parameters for GA4 add_to_cart event
 */
export const createAddToCartParams = (input: CreateAddToCartParamsInput): AddToCartParams => {
  const { product, selectedOptionProduct, quantity, currency, deviceId } = input;

  const itemPrice = selectedOptionProduct.salePrice ?? selectedOptionProduct.originalPrice;
  const totalPrice = (itemPrice * quantity).toString();

  const items = createGA4ItemsFromProduct(product);

  return {
    'x-fb-cd-content_type': 'product',
    'x-fb-cd-content_name': selectedOptionProduct.name,
    'x-fb-cd-content_ids': selectedOptionProduct.productId.toString(),
    value: totalPrice,
    currency,
    items,
    event_name: 'add_to_cart',
    event_time: Math.round(new Date().getTime() / 1000),
    item_variant: `${selectedOptionProduct.size}(${selectedOptionProduct.pack})`,
    product_sku: selectedOptionProduct.sku,
    ...(deviceId && { device_id: deviceId }),
  };
};

// ============================================
// Buy Now (Click) Event
// ============================================

/**
 * Parameters for click_buy_now event
 */
export interface BuyNowParams {
  quantity: number;
  product_sku: string;
  product_price: number | undefined;
  item_variant: string;
  product_name: string;
  [key: string]:
    | string
    | number
    | boolean
    | undefined
    | null
    | string[]
    | Record<string, string | number | boolean | undefined>
    | Record<string, string | number | boolean | undefined>[];
}

export interface CreateBuyNowParamsInput {
  selectedOptionProduct: SimpleProductDetail | VariationProductDetail;
  quantity: number;
}

/**
 * Creates the logging parameters for click_buy_now event
 */
export const createBuyNowParams = (input: CreateBuyNowParamsInput): BuyNowParams => {
  const { selectedOptionProduct, quantity } = input;

  return {
    quantity,
    product_sku: selectedOptionProduct.sku,
    product_price: selectedOptionProduct.salePrice ?? selectedOptionProduct.originalPrice,
    item_variant: `${selectedOptionProduct.size}(${selectedOptionProduct.pack})`,
    product_name: selectedOptionProduct.name,
  };
};
